/**
 * Browsers often report CSV as text/plain, application/vnd.ms-excel,
 * or an empty type — especially files saved from Excel / Google Sheets.
 */
export function isVenueCsvFile(file) {
  if (!file || typeof file.name !== 'string') return false;
  const name = file.name.trim().toLowerCase();
  const type = String(file.type || '').toLowerCase();
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return false;
  if (name.endsWith('.csv')) return true;
  if (type.includes('csv')) return true;
  return false;
}

export function csvRejectReason(file) {
  if (!file || typeof file.name !== 'string') {
    return 'No file selected. Please choose a CSV file.';
  }
  const name = file.name.trim().toLowerCase();
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return 'This looks like an Excel workbook. Export it as CSV (File → Save As → CSV) and try again.';
  }
  if (!isVenueCsvFile(file)) {
    return `Invalid file type. Got ${file.type || 'unknown'}, expected a .csv file.`;
  }
  return null;
}

const EXACT_HEADER_MAP = {
  'STATE/COUNTRY': 'state',
  'VENUE': 'name',
  'CITY': 'city',
  'WEBSITE': 'website',
  'CAPACITY': 'capacity',
  'AFFILIATE': 'affiliate',
  'STAGE': 'stage',
  'SPEC SHEET LINK': 'specSheetLink',
  'POWER': 'power',
  'LIGHTING CONTROL': 'lightingControl',
  'LIGHTING FIXTURES': 'lightingFixtures',
  'VIDEO': 'video',
  'FOH': 'foh',
  'PA': 'pa',
  'MONITORS': 'monitors',
  'BACKSTAGE': 'backstage',
  'BACKLINE': 'backline',
  'MERCH': 'merch',
  'PARKING': 'parking',
  'SHORE POWER': 'shorePower',
  'NOTES': 'notes',
  'MAP Embed Link': 'mapEmbedLink',
  'MAP EMBED LINK': 'mapEmbedLink',
  'Map Embed Link': 'mapEmbedLink',
  'EMBED': 'mapEmbedLink',
  'MAP': 'mapEmbedLink',
};

function normalizeHeaderKey(header) {
  return String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Map a spreadsheet header to the venue field the importer writes.
 * Unmapped headers are returned trimmed so extra columns still import.
 */
export function mapVenueCsvHeader(header) {
  if (header == null) return null;
  const trimmed = String(header).replace(/^\uFEFF/, '').trim();
  if (!trimmed) return null;
  if (EXACT_HEADER_MAP[trimmed]) return EXACT_HEADER_MAP[trimmed];

  const key = normalizeHeaderKey(trimmed);
  if (key === 'state' || key === 'country' || key === 'state/country' || key === 'state country') {
    return 'state';
  }
  if (key === 'venue' || key === 'name' || key === 'venue name' || key === 'venue title') {
    return 'name';
  }
  if (key === 'city' || key === 'town') {
    return 'city';
  }
  if (
    key === 'map' ||
    key === 'google' ||
    key === 'maps' ||
    key === 'embed' ||
    key === 'map embed' ||
    key === 'map embed link' ||
    key === 'embed link' ||
    key === 'google maps' ||
    key === 'google map'
  ) {
    return 'mapEmbedLink';
  }
  if (key === 'spec sheet' || key === 'spec sheet link' || key === 'specsheet') {
    return 'specSheetLink';
  }
  if (key === 'lighting control') return 'lightingControl';
  if (key === 'lighting fixtures') return 'lightingFixtures';
  if (key === 'shore power') return 'shorePower';

  return EXACT_HEADER_MAP[trimmed.toUpperCase()] || trimmed;
}
